import { Injectable } from '@jkhy/frontend-utils/di';
import { DashboardService, DashboardWidget } from '../../dashboard';
import { Entitlements, EntitlementsService } from '../../entitlements';
import { NavigationItem, NavigationsService } from '../../navigation';
import { RemoteDepositCaptureService } from '../../remote-deposit-capture';
import { AuthenticationService, TmUser } from '../authentication';
import { DeviceService } from '../device/device.service';
import { Feature, FeatureFlagService } from '../feature-flags';
import {
    BoVisibilityFeature,
    BoVisibilityFeatureConditions,
} from './conditions/feature-visibility.backoffice.types';
import {
    DeviceType,
    ProductType,
    PwaVisibilityFeature,
    PwaVisibilityFeatureConditions,
    WidgetType,
} from './conditions/feature-visibility.pwa.types';

@Injectable()
export class FeatureVisibilityService {
    constructor(
        private featureFlagService: FeatureFlagService,
        private entitlementsService: EntitlementsService,
        private authService: AuthenticationService,
        private navigationsService?: NavigationsService,
        private dashboardService?: DashboardService,
        private rdcService?: RemoteDepositCaptureService,
        private deviceService?: DeviceService
    ) {}

    private visibilityFeatureConditionMap: Map<
        PwaVisibilityFeature | BoVisibilityFeature,
        PwaVisibilityFeatureConditions | BoVisibilityFeatureConditions
    > = new Map();

    private initialized = false;

    private configuredProducts: Map<ProductType, boolean> = new Map();

    private configuredWidgets: WidgetType[] = [];

    private rdcUserConfigured: boolean = false;

    private entitlementsMap: Map<Entitlements, boolean> = new Map();

    private navigationOptions: NavigationItem[] = [];

    private visibilityFeature: Map<PwaVisibilityFeature | BoVisibilityFeature, boolean> = new Map();

    private isAdmin: boolean = false;

    private isUisEnabled = false;

    public async init(
        visibilityFeatureConditionMap: Map<
            PwaVisibilityFeature | BoVisibilityFeature,
            PwaVisibilityFeatureConditions | BoVisibilityFeatureConditions
        >
    ) {
        if (this.initialized) {
            return;
        }

        this.visibilityFeatureConditionMap = visibilityFeatureConditionMap;

        const promises: Array<Promise<unknown>> = [
            this.entitlementsService.getEntitlements(),
            this.authService.authenticated$.toPromise(true),
        ];

        if (this.navigationsService) {
            promises.push(this.navigationsService?.getFlattenedNavigationOptions());
        }

        if (this.dashboardService) {
            promises.push(this.dashboardService?.getConfiguredProducts());
            promises.push(this.dashboardService?.getConfiguredWidgets());
        }

        if (this.rdcService) {
            promises.push(this.rdcService?.isUserConfiguredCorrectly());
        }

        const responses = await Promise.all(promises);

        this.entitlementsMap = responses.shift() as Map<Entitlements, boolean>;
        this.isAdmin = (responses.shift() as TmUser).isAdmin;

        if (this.navigationsService) {
            this.navigationOptions = responses.shift() as NavigationItem[];
        }

        if (this.dashboardService) {
            this.configuredProducts = responses.shift() as Map<ProductType, boolean>;
            this.configuredWidgets = (responses.shift() as DashboardWidget[]).map(
                widget => widget.type
            ) as WidgetType[];
        }
        if (this.rdcService) {
            this.rdcUserConfigured = responses.shift() as boolean;
        }

        this.isUisEnabled = await this.authService.uisMetadataPromise.then(data =>
            typeof data !== 'undefined' ? (data.isUisEnabled ?? false) : false
        );

        await this.featureFlagService.ready$.toPromise();
        await this.determineFeaturesVisibility();

        //Listen for FF overrides and then re-check features for visibility
        (await this.featureFlagService).featureChanged$.subscribe(async () => {
            await this.determineFeaturesVisibility();
        });

        //Listen for widget changes and then re-check features for visibility
        if (this.dashboardService) {
            this.dashboardService.widgetChanged$.subscribe(async (widgets: DashboardWidget[]) => {
                this.configuredWidgets = widgets.map(widget => widget.type) as WidgetType[];
                await this.determineFeaturesVisibility();
            });
        }

        this.initialized = true;
    }

    public isFeatureVisible(feature: PwaVisibilityFeature | BoVisibilityFeature): boolean {
        return !this.initialized ? false : !!this.visibilityFeature.get(feature);
    }

    private async determineFeaturesVisibility() {
        this.visibilityFeatureConditionMap?.forEach(async (conditions, key) => {
            const isAdminConditionsMet =
                !('isAdmin' in conditions) || conditions.isAdmin === this.isAdmin;

            const navigationConditionsMet =
                !this.navigationsService ||
                !('navigationKeys' in conditions) ||
                this.navigationConditionsMet(
                    conditions.navigationKeys as NavigationItem | NavigationItem[]
                );

            const productTypeConditionsMet =
                !this.dashboardService ||
                !('productTypes' in conditions) ||
                this.productTypeConditionsMet(
                    conditions.productTypes as ProductType | ProductType[]
                );

            const entitlementConditionsMet =
                !('entitlements' in conditions) ||
                this.entitlementConditionsMet(
                    conditions.entitlements as Entitlements | Entitlements[]
                );

            const featureFlagConditionsMet =
                !('featureFlags' in conditions) || (await this.featureFlagConditionsMet(key));

            const widgetTypeConditionsMet =
                !this.dashboardService ||
                !('widgetType' in conditions) ||
                this.configuredWidgets.includes(conditions.widgetType as WidgetType);

            const rdcConditionsMet =
                !this.rdcService ||
                !('rdcUserConfigured' in conditions) ||
                conditions.rdcUserConfigured === this.rdcUserConfigured;

            const deviceTypeConditionsMet =
                !this.deviceService ||
                !('deviceType' in conditions) ||
                this.deviceTypeConditionsMet(conditions.deviceType as DeviceType);

            const uisConditionMet = 'uisEnabled' in conditions ? this.isUisEnabled : true;

            const isVisible =
                isAdminConditionsMet &&
                navigationConditionsMet &&
                productTypeConditionsMet &&
                entitlementConditionsMet &&
                featureFlagConditionsMet &&
                widgetTypeConditionsMet &&
                rdcConditionsMet &&
                deviceTypeConditionsMet &&
                uisConditionMet;

            this.visibilityFeature.set(key, isVisible);
        });
    }

    private navigationConditionsMet(navigationKeys: NavigationItem | NavigationItem[]) {
        if (Array.isArray(navigationKeys)) {
            return navigationKeys.some(navigationKey => {
                return !!this.navigationOptions.find(option => option === navigationKey);
            });
        }
        return !!this.navigationOptions.find(option => option === navigationKeys);
    }

    private entitlementConditionsMet(entitlements: Entitlements | Entitlements[]) {
        if (Array.isArray(entitlements)) {
            return entitlements.some(entitlement => {
                return this.entitlementsMap.get(entitlement);
            });
        }
        return !!this.entitlementsMap.get(entitlements);
    }

    private async featureFlagConditionsMet(
        visibilityFeature: PwaVisibilityFeature | BoVisibilityFeature
    ) {
        if (!this.visibilityFeatureConditionMap?.has(visibilityFeature)) {
            return false;
        }

        const conditions = this.visibilityFeatureConditionMap.get(visibilityFeature) as
            | PwaVisibilityFeatureConditions
            | BoVisibilityFeatureConditions;
        if ('featureFlags' in conditions) {
            const featureFlagService = await this.featureFlagService;
            const flags = conditions.featureFlags as Feature | Feature[];
            if (Array.isArray(flags)) {
                // All flags must be enabled (AND). Unlike other array conditions which use OR,
                // multiple feature flags represent a conjunction of gates.
                const results = await Promise.all(
                    flags.map(flag => featureFlagService.isEnabled(flag))
                );
                return results.every(Boolean);
            }
            return featureFlagService.isEnabled(flags);
        }
        return true;
    }

    private productTypeConditionsMet(productTypes: ProductType | ProductType[]) {
        if (Array.isArray(productTypes)) {
            return productTypes.some(productType => {
                return this.configuredProducts.get(productType);
            });
        }
        return !!this.configuredProducts.get(productTypes);
    }

    private deviceTypeConditionsMet(deviceType: DeviceType) {
        if (!this.deviceService) {
            return true;
        }
        switch (deviceType) {
            case DeviceType.DesktopOnly:
                return this.deviceService.isDesktop;
            case DeviceType.MobileOnly:
                return !this.deviceService.isDesktop;
            default:
                return true;
        }
    }
}
